// Wire quantizers (§9.3). ALL quantizers SATURATE (clamp to range), never wrap (T-PROTO-013). LSBs
// and ranges are protocol constants (not gameplay tunables). Little-endian handled by the codecs.

export const QUANT = {
  POS_X: { min: -60, max: 60 }, // u16 ⇒ ~1.83 mm
  POS_Y: { min: -40, max: 40 }, // u16 ⇒ ~1.22 mm
  Z_LSB: 0.02, // player z, u8 (2 cm), 0..5.1 m
  Z_MAX_U8: 255,
  VEL_LSB: 0.15, // i8 ⇒ ±19.05 m/s (covers slide 15.12, ragdoll ≤18.8)
  FACING: { min: 0, max: Math.PI * 2 }, // u8
  STAMINA: { min: 0, max: 1 }, // u8
  BALL_Z: { min: 0, max: 30 }, // u16
  BALL_VEL_LSB: 0.01, // i16
  SPIN_LSB: 0.5, // i8
  SPIN_MAX: 60, // |ω| clamp before quantize
  MOVE_LSB: 1 / 127, // i8 for moveX/Y ∈ [−1,1]
} as const;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Map [min,max] → u16 [0,65535], saturating. */
export function quantU16(v: number, min: number, max: number): number {
  const t = (v - min) / (max - min);
  return clamp(Math.round(t * 65535), 0, 65535);
}
export function dequantU16(q: number, min: number, max: number): number {
  return min + (q / 65535) * (max - min);
}

/** Map value → u8 [0,255] over [min,max], saturating. */
export function quantU8(v: number, min: number, max: number): number {
  const t = (v - min) / (max - min);
  return clamp(Math.round(t * 255), 0, 255);
}
export function dequantU8(q: number, min: number, max: number): number {
  return min + (q / 255) * (max - min);
}

/** value/lsb → i8 [−127,127], saturating (−128 reserved). */
export function quantI8(v: number, lsb: number): number {
  return clamp(Math.round(v / lsb), -127, 127);
}
export function dequantI8(q: number, lsb: number): number {
  return q * lsb;
}

/** value/lsb → i16 [−32767,32767], saturating. */
export function quantI16(v: number, lsb: number): number {
  return clamp(Math.round(v / lsb), -32767, 32767);
}
export function dequantI16(q: number, lsb: number): number {
  return q * lsb;
}

/** Facing angle → u8, wrapped into [0,2π) first then mapped (angles are periodic, not saturated). */
export function quantFacing(a: number): number {
  const twoPi = Math.PI * 2;
  let x = a % twoPi;
  if (x < 0) x += twoPi;
  return clamp(Math.round((x / twoPi) * 256), 0, 255) & 0xff;
}
export function dequantFacing(q: number): number {
  return (q / 256) * Math.PI * 2;
}

/** Angle → u16, wrapped into [0,2π) (periodic). */
export function quantAngleU16(a: number): number {
  const twoPi = Math.PI * 2;
  let x = a % twoPi;
  if (x < 0) x += twoPi;
  return clamp(Math.round((x / twoPi) * 65535), 0, 65535);
}
export function dequantAngleU16(q: number): number {
  return (q / 65535) * Math.PI * 2;
}

/** Spin component → i8, clamped to the |ω| range FIRST so it saturates to the semantic bound (§9.3). */
export function quantSpin(v: number): number {
  return quantI8(clamp(v, -QUANT.SPIN_MAX, QUANT.SPIN_MAX), QUANT.SPIN_LSB);
}
export function dequantSpin(q: number): number {
  return dequantI8(q, QUANT.SPIN_LSB);
}
